import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { RedDocumento } from '../../../types/red'
import { FlagProgettazioneVoce } from '../../../constants/red'
import { redApi } from '../../../services/api'
import { downloadBlobResponse } from '../../../lib/downloadBlob'

interface Props {
  immobileId: string
  fase: string
  voce: FlagProgettazioneVoce
  documentiEsistenti: RedDocumento[]
  onClose: () => void
  onUploaded: () => void
}

const UploadDocumentoFlagModal: React.FC<Props> = ({ immobileId, fase, voce, documentiEsistenti, onClose, onUploaded }) => {
  const [file, setFile] = useState<File | null>(null)
  const [nomeFile, setNomeFile] = useState('')
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const handleDownload = async (doc: RedDocumento) => {
    try {
      const res = await redApi.documenti.download(doc.id)
      downloadBlobResponse(res, doc.titolo)
    } catch {
      toast.error('Errore durante il download del documento')
    }
  }

  const handleDelete = async (doc: RedDocumento) => {
    if (!confirm(`Eliminare "${doc.titolo}"?`)) return
    setDeletingId(doc.id)
    try {
      await redApi.documenti.delete(doc.id)
      toast.success('Documento eliminato')
      onUploaded()
    } catch {
      toast.error('Errore eliminazione documento')
    } finally {
      setDeletingId(null)
    }
  }

  const handleCarica = async () => {
    if (!file) {
      toast.error('Seleziona un file da caricare')
      return
    }
    setSaving(true)
    try {
      const doc = await redApi.documenti.createPlaceholder({
        immobile_id: immobileId,
        fase,
        tipologia: voce.tipologia,
        titolo: nomeFile.trim() || voce.label,
      })
      const documentoId = doc.data.id
      try {
        await redApi.documenti.upload(documentoId, file)
      } catch (err) {
        await redApi.documenti.delete(documentoId).catch(() => {})
        throw err
      }
      if (voce.field) {
        try {
          await redApi.immobili.update(immobileId, { [voce.field]: true })
        } catch {
          toast('Documento caricato, salvare l\'anagrafica per confermare il flag', { icon: '⚠️' })
        }
      }
      toast.success('Documento caricato')
      setFile(null)
      setNomeFile('')
      onUploaded()
    } catch {
      toast.error('Errore caricamento documento')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !saving && onClose()}>
      <div className="bg-white rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-3">Documenti: {voce.label}</h3>

        {documentiEsistenti.length > 0 && (
          <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
            {documentiEsistenti.map(doc => (
              <div key={doc.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
                <span className="truncate mr-2">{doc.titolo}</span>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => handleDownload(doc)} className="text-blue-600 hover:underline">Scarica</button>
                  <button onClick={() => handleDelete(doc)} disabled={deletingId === doc.id} className="text-red-500 hover:underline">Elimina</button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-3 border-t border-gray-200 pt-3">
          <p className="text-sm text-gray-500">Aggiungi un nuovo file per questa voce:</p>
          <div>
            <label className="label">Nome file</label>
            <input
              type="text"
              className="input"
              value={nomeFile}
              onChange={e => setNomeFile(e.target.value)}
              placeholder={voce.label}
            />
          </div>
          <input
            type="file"
            className="input"
            onChange={e => setFile(e.target.files?.[0] || null)}
          />
          <div className="flex gap-2">
            <button onClick={handleCarica} disabled={saving || !file} className="btn-primary text-sm">
              {saving ? 'Caricamento...' : 'Carica'}
            </button>
            <button onClick={onClose} disabled={saving} className="text-sm text-gray-500">Chiudi</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UploadDocumentoFlagModal
